import React, { useContext } from 'react';
import Stage1 from './Stage1';
import Stage2 from './Stage2';
import Stage3 from './Stage3';
import Stage4 from './Stage4';
import Stage5 from './Stage5';
import Stage6 from './Stage6';
import Stage7 from './Stage7';

import MenuContext from './MenuContext';
import { useFormContext } from "./FormContext";
import { validateStage1, validateStage2 } from './validation';
import PageHeader from '../common/PageHeader';

const steps = [
  { label: 'Initialize Workflow', short: 'Initialize' },
  { label: 'Data & Time Configuration', short: 'Data & Time' },
  { label: 'Define Dimensions', short: 'Dimensions' },
  { label: 'Data Validation', short: 'Validation' },
  { label: 'Event & Seasonality Layer', short: 'Seasonality' },
  { label: 'Model Selection', short: 'Models' },
  { label: 'Review & Execute', short: 'Review' },
];

function Workflowtabs() {
  const { activeTab, setActiveTab } = useContext(MenuContext);
  const { formData, updateFormData } = useFormContext();

  const activeIndex = parseInt(activeTab.replace('Stage', ''), 10) - 1;

  const handleStepClick = (idx) => {
    const menu = `Stage${idx + 1}`;
    const activeTabNumber = activeIndex + 1;

    if (activeTabNumber === 1) {
      const { isValid, errors } = validateStage1(formData.stage1);
      updateFormData("stage1", { ...formData.stage1, isValid, errors });
    } else if (activeTabNumber === 2) {
      const { isValid, errors } = validateStage2(formData.stage2);
      updateFormData("stage2", { ...formData.stage2, isValid, errors });
    }

    setActiveTab(menu);
  };

  const renderStepContent = () => {
    switch (activeTab) {
      case 'Stage1': return <Stage1 />;
      case 'Stage2': return <Stage2 />;
      case 'Stage3': return <Stage3 />;
      case 'Stage4': return <Stage4 />;
      case 'Stage5': return <Stage5 />;
      case 'Stage6': return <Stage6 />;
      case 'Stage7': return <Stage7 />;
      default: return null;
    }
  };

  return (
    <div className="wf-page">
      <PageHeader
        title="Build Your Forecast"
        subtitle="Configure your workflow step by step."
        actions={
          <div className="wf-step-badge">
            Step {activeIndex + 1} of {steps.length}
          </div>
        }
      />

      <div className="wf-nav-card">
        <div className="wf-active-label">
          <span>Current: </span>
          {steps[activeIndex].label}
        </div>
        <div className="wf-pills" role="tablist" aria-label="Workflow steps">
          {steps.map((step, idx) => {
            const isActive = idx === activeIndex;
            const isComplete = idx < activeIndex;
            const stateClass = isActive ? "is-active" : isComplete ? "is-complete" : "";
            const lineDone = idx <= activeIndex;

            return (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <div
                    className={`wf-pill-line${lineDone ? " is-done" : ""}`}
                    aria-hidden="true"
                  />
                )}
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`wf-pill ${stateClass}`}
                  onClick={() => handleStepClick(idx)}
                  title={step.label}
                >
                  {isComplete ? `✓ ${step.short}` : step.short}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div>{renderStepContent()}</div>

      <style>{workflowPageStyles}</style>
    </div>
  );
}

export default Workflowtabs;

// ==================== CREATE WORKFLOW NAV STYLES ====================

const workflowPageStyles = `
  .wf-page {
    width: 100%;
  }

  /* Beat global label/h3 rules so all stages match stageFormStyles */
  .wf-page label {
    font-family: Poppins, sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #0c3c54 !important;
    margin-bottom: 8px !important;
    text-transform: none !important;
  }

  .wf-page label.wf-option-label,
  .wf-page .select-all-label,
  .wf-page .checkbox-row label {
    font-size: 13px !important;
    font-weight: 500 !important;
    margin-bottom: 0 !important;
  }

  .wf-page h3 {
    font-family: Poppins, sans-serif !important;
    font-size: 14px !important;
    font-weight: 600 !important;
  }

  .wf-page .lblerror,
  .wf-page label.text-red-600 {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #dc2626 !important;
    margin-bottom: 0 !important;
  }

  .wf-step-badge {
    flex-shrink: 0;
    font-family: Poppins, sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #0c3c54;
    background: #eff7fc;
    border: 1px solid #c4e0f0;
    border-radius: 999px;
    padding: 6px 12px;
    white-space: nowrap;
  }

  .wf-nav-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e6eef4;
    box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
    padding: 12px 14px;
    margin-bottom: 14px;
  }

  .wf-active-label {
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #0c3c54;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e6eef4;
  }

  .wf-active-label span {
    color: #6a7d8c;
    font-weight: 500;
  }

  .wf-pills {
    display: flex;
    align-items: center;
    gap: 0;
    width: 100%;
  }

  .wf-pill-line {
    flex: 1 1 12px;
    min-width: 10px;
    height: 2px;
    background: #e6eef4;
    margin: 0 4px;
    transition: background 0.2s ease;
  }

  .wf-pill-line.is-done {
    background: #368E60;
  }

  .wf-pill {
    flex: 1 1 0;
    min-width: 0;
    padding: 7px 6px;
    border-radius: 999px;
    border: 1px solid #e6eef4;
    background: #f6fafd;
    color: #6a7d8c;
    font-family: Poppins, sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  @media (max-width: 900px) {
    .wf-pills {
      overflow-x: auto;
      padding-bottom: 2px;
      scrollbar-width: thin;
    }

    .wf-pill {
      flex: 0 0 auto;
      width: auto;
      padding: 7px 12px;
    }

    .wf-pill-line {
      flex: 0 0 16px;
      min-width: 16px;
    }
  }

  .wf-pill:hover {
    border-color: #c4e0f0;
    color: #0c3c54;
    background: #eff7fc;
  }

  .wf-pill.is-complete {
    background: #368E60;
    color: #fff;
    border-color: #2f7a53;
    box-shadow: 0 2px 8px rgba(54, 142, 96, 0.28);
  }

  .wf-pill.is-complete:hover {
    background: #2f7a53;
    color: #fff;
    border-color: #2f7a53;
  }

  .wf-pill.is-active {
    background: #032B4E;
    color: #fff;
    border-color: #032B4E;
    box-shadow: 0 3px 10px rgba(3, 43, 78, 0.18);
  }
`;
